import React from "react";
import PropTypes from "prop-types";
import CountUp from "react-countup";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { toFixed } from "utils/helpers";

import "./Home.scss";

// The "Portfolio Performance" KPI cards (/trades/performance-stats response),
// shared by the Dashboard and the Trading page.
// compact: 4 tiles + one line (Trading page); periodLabel: e.g. "All time"
const PerformanceStatsCards = ({
  performanceStats,
  compact = false,
  periodLabel = "All time",
}) => {
  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    return `$${parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercentage = (value) => {
    if (!value) return "0%";
    return `${toFixed(+value, 2)}%`;
  };

  const getColorClass = (value) => {
    if (!value) return "";
    const numValue = parseFloat(value);
    if (numValue === 0) return "";
    if (numValue > 0) return "green";
    return "red";
  };

  const getStreakColor = (type, streak) => {
    if (streak === 0) return "";
    if (type === "win") return "green";
    return "red";
  };

  const totalPnlCard = (
    <GenericCard minHeight={120} title="Total P&L" disableToggle>
      <div className="flex flex-col gap-2">
        <div
          className={`text-3xl font-bold ${getColorClass(
            performanceStats?.totalPnl
          )}`}
        >
          <CountUp
            prefix="$"
            end={performanceStats?.totalPnl || 0}
            separator=","
            duration={2}
            className={getColorClass(performanceStats?.totalPnl)}
          />
        </div>
        <div className="text-sm">{periodLabel} profit/loss</div>
      </div>
    </GenericCard>
  );

  const winRateCard = (
    <GenericCard minHeight={85} title="Win Rate" disableToggle>
      <div className="flex flex-col gap-2">
        <div className="text-3xl font-bold text-blue-600">
          {formatPercentage(performanceStats?.winRate)}
        </div>
        <div className="text-sm">Success rate</div>
      </div>
    </GenericCard>
  );

  const totalTradesCard = (
    <GenericCard minHeight={85} title="Total Trades" disableToggle>
      <div className="flex flex-col gap-2">
        <div className="text-3xl font-bold text-gray-800">
          {performanceStats?.closedTrades || 0}
        </div>
        <div className="text-sm">Closed trades</div>
      </div>
    </GenericCard>
  );

  const activePositionsCard = (
    <GenericCard minHeight={85} title="Active Positions" disableToggle>
      <div className="flex flex-col gap-2">
        <div className="text-3xl font-bold text-purple-600">
          {performanceStats?.activePositions || 0}
        </div>
        <div className="text-sm">
          Value: {formatCurrency(performanceStats?.activePositionsValue)}
        </div>
      </div>
    </GenericCard>
  );

  const unrealizedCard = (
    <GenericCard minHeight={85} title="Unrealized P&L" disableToggle>
      <div className="flex flex-col gap-2">
        <div
          className={`text-2xl font-bold ${getColorClass(
            performanceStats?.unrealizedPnl
          )}`}
        >
          {formatCurrency(performanceStats?.unrealizedPnl)}
        </div>
        <div
          className={`pt-1 text-sm ${getColorClass(
            performanceStats?.unrealizedPnlPercentage
          )}`}
        >
          {formatPercentage(performanceStats?.unrealizedPnlPercentage)}
        </div>
      </div>
    </GenericCard>
  );

  const profitFactorCard = (
    <GenericCard minHeight={85} title="Profit Factor" disableToggle>
      <div className="flex flex-col gap-2">
        <div className="text-2xl font-bold text-indigo-600">
          {performanceStats?.profitFactor || 0}
        </div>
      </div>
    </GenericCard>
  );

  const streakCard = (
    <GenericCard minHeight={85} title="Current Streak" disableToggle>
      <div className="flex flex-col gap-2">
        {performanceStats?.currentStreak === 0 ? (
          <div className="text-2xl font-bold text-gray-600">0</div>
        ) : (
          <div
            className={`text-2xl font-bold ${getStreakColor(
              performanceStats?.currentStreakType,
              performanceStats?.currentStreak
            )}`}
          >
            {performanceStats?.currentStreak || 0}{" "}
            {performanceStats?.currentStreakType || "win"}s
          </div>
        )}
      </div>
    </GenericCard>
  );

  const streakText =
    performanceStats?.currentStreak > 0
      ? `${performanceStats.currentStreak} ${
          performanceStats.currentStreakType || "win"
        }s`
      : "0";

  if (compact) {
    // Trading page: the 4 key numbers as tiles, the rest on one line
    return (
      <>
        <section className="stats-grid stats-grid-compact">
          {totalPnlCard}
          {unrealizedCard}
          {winRateCard}
          {activePositionsCard}
        </section>
        <div className="stats-line">
          <span>
            Closed trades: <b>{performanceStats?.closedTrades || 0}</b>
          </span>
          <span>
            Profit factor: <b>{performanceStats?.profitFactor || 0}</b>
          </span>
          <span>
            Current streak:{" "}
            <b
              className={getStreakColor(
                performanceStats?.currentStreakType,
                performanceStats?.currentStreak,
              )}
            >
              {streakText}
            </b>
          </span>
        </div>
      </>
    );
  }

  return (
    <section className="stats-grid">
      {totalPnlCard}
      {winRateCard}
      {totalTradesCard}
      {activePositionsCard}
      {unrealizedCard}
      {profitFactorCard}
      {streakCard}
    </section>
  );
};

PerformanceStatsCards.propTypes = {
  performanceStats: PropTypes.object,
  compact: PropTypes.bool,
  periodLabel: PropTypes.string,
};

export default PerformanceStatsCards;
